import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface AuthUser {
  id: number
  email: string
  roles: string[]
}

export const useAuthStore = defineStore('auth', () => {
  const user    = ref<AuthUser | null>(null)
  const checked = ref(false)

  async function fetchMe(): Promise<void> {
    try {
      const res = await fetch('/api/auth/me', {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
      user.value = res.ok ? await res.json() : null
    } catch {
      user.value = null
    } finally {
      checked.value = true
    }
  }

  async function login(email: string, password: string): Promise<void> {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      const d = data as { error?: string; message?: string }
      throw new Error(d.error ?? d.message ?? 'Invalid credentials.')
    }

    // The login controller returns the user object on success (200)
    user.value = await res.json() as AuthUser
    checked.value = true
  }

  async function register(email: string, password: string): Promise<void> {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({} as { errors?: Record<string, string> }))
      const errs = (data as { errors?: Record<string, string> }).errors
      const msg  = errs ? Object.values(errs)[0] : 'Registration failed.'
      throw new Error(msg)
    }

    // Auto-login after successful registration
    await login(email, password)
  }

  async function logout(): Promise<void> {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    })
    user.value = null
  }

  return { user, checked, fetchMe, login, register, logout }
})
